import {
  FilterDocumentDateCustomTypes,
  FilterDocumentDateFilterTypes,
  ID,
  Task,
} from '@griply/types';
import {
  getOverdueTasksFromTree,
  onUpcomingDeadlineReorder,
} from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useCallback, useMemo } from 'react';
import {
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTreeValue,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useShouldHaveRestOfMonth } from 'shared/hooks/use-should-have-rest-of-month';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';

import {
  upcomingMonthPrefix,
  upcomingRestOfMonthPrefix,
  upcomingYearPrefix,
  useUpcomingDeadlineTree,
} from '../../hooks/use-upcoming-deadline-tree';
import { GroupedListProps } from '../../types';
import * as Styled from './upcoming-deadline-grouping-tasks-list.style';
import {
  UpcomingDeadlineRenderData,
  UpcomingDeadlineRenderDataContext,
} from './upcoming-deadline-render-context';
import { UpcomingDeadlineRenderItem } from './upcoming-deadline-render-item';

const taskItemTypes = [TaskListItemTypes.Task];

const sectionPrefixes = {
  restOfMonth: upcomingRestOfMonthPrefix,
  month: upcomingMonthPrefix,
  year: upcomingYearPrefix,
};

export const UpcomingDeadlineGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  filterDocument,
  isContextMenuOpen,
  defaultNewTaskDate,
}) => {
  const today = useToday();
  const { submit: updateTasks } = useUpdateTasks();
  const { shouldHaveRestOfMonth, restOfMonthStartDate } =
    useShouldHaveRestOfMonth();

  // Extract dateFilterEndDate from filter document (same pattern as deadline-grouping)
  const dateFilterEndDate = useMemo(() => {
    const directDateFilter = filterDocument?.filters?.date;
    if (
      directDateFilter?.type === FilterDocumentDateFilterTypes.Custom &&
      directDateFilter.custom?.type === FilterDocumentDateCustomTypes.Before
    ) {
      return directDateFilter.custom.end;
    }

    const orBranches = filterDocument?.or ?? [];
    const matchedBranch = orBranches.find(
      (branch) =>
        branch.date?.type === FilterDocumentDateFilterTypes.Custom &&
        branch.date.custom?.type === FilterDocumentDateCustomTypes.Before,
    );

    return matchedBranch?.date?.custom?.end ?? undefined;
  }, [filterDocument]);

  const showCompleted = !!filterDocument?.showCompleted;

  const treeData = useUpcomingDeadlineTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    dateFilterEndDate,
    hideNewTaskRow: groupSettings?.hideNewTaskRow,
    shouldHaveRestOfMonth,
    restOfMonthStartDate,
    showCompleted,
  });

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const _onReorder = useCallback(
    (updatedItems: TreeItem[]) => {
      if (!onReorder) {
        return;
      }

      const result = onUpcomingDeadlineReorder({
        updatedItems,
        tasksMap,
        today,
        weekStartsOn,
        prefixes: sectionPrefixes,
      });

      onReorder(result.updatedSections, result.updatedTasks);
    },
    [onReorder, tasksMap, today, weekStartsOn],
  );

  const displayDue = groupSettings?.displayDue;
  const externalEvents = groupSettings?.externalEvents;
  const timeFormat = groupSettings?.timeFormat;

  const overdueTasks = useMemo(
    () => getOverdueTasksFromTree(treeData, tasksMap),
    [treeData, tasksMap],
  );

  const renderData = useMemo<UpcomingDeadlineRenderData>(
    () => ({
      tasksMap,
      tasks,
      overdueTasks,
      externalEvents,
      today,
      denseFormat,
      displayDue,
      timeFormat,
      showCompleted,
    }),
    [
      tasksMap,
      tasks,
      overdueTasks,
      externalEvents,
      today,
      denseFormat,
      displayDue,
      timeFormat,
      showCompleted,
    ],
  );

  const tree = useTreeValue();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <UpcomingDeadlineRenderDataContext.Provider value={renderData}>
        <VirtualizedTreeList
          rootItemId={rootId}
          items={treeData}
          renderItem={UpcomingDeadlineRenderItem}
          scrollElement={scrollElement}
          multiselectTypes={taskItemTypes}
          dragTypes={taskItemTypes}
          onReorder={_onReorder}
          collapsedItems={collapsedSections}
          onCollapse={onSectionCollapse}
          denseFormat={denseFormat}
          isContextMenuOpen={isContextMenuOpen}
        />
      </UpcomingDeadlineRenderDataContext.Provider>
    </Styled.Container>
  );
};
